import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { getConstellation, getContributions, saveDesigns, supabase } from "./supabaseClient";
import CrystalCompositor, {
  LAYOUT_PRESET_NAMES, CUSTOM_BACKGROUNDS, CUSTOM_TEXT_COLORS, CUSTOM_TEXTURES,
  CUSTOM_FINISHES, CUSTOM_TYPE_SETS, CUSTOM_NAME_PLACEMENTS, CUSTOM_QUOTE_ALIGNS,
  CUSTOM_STAR_MARKS, CUSTOM_BORDERS, CUSTOM_NAME_CASES,
  MOSAIC_SHAPES, MOSAIC_ZODIAC, MOSAIC_INKS, MOSAIC_GROUNDS, MOSAIC_DEFAULTS, GRID_DEFAULTS,
  CUSTOM_DEFAULTS, contrastRatio, autoTextColor, luminance,
} from "./CrystalCompositor";
import { downloadPDF } from "./downloadPDF";
import { captureAndUpload } from "./captureAndUpload";
import { useTheme, PageWrap, ThemeToggle, Starfield, SERIF, SANS, PLAY, btnStyle, LineDivider } from "./theme";

var API_URL = "/api/chat", API_KEY = "yt_sculpt_2026_beta";

var BRAIN_TEMPLATE = function(numContributors) {
  var starCount = numContributors <= 6 ? 6 : numContributors;
  var coverageRule = numContributors >= 6
    ? 'COVERAGE IS THE MOST IMPORTANT RULE. There are ' + numContributors + ' contributors and ' + starCount + ' stars. Give EVERY contributor EXACTLY ONE star. Never merge two contributors into one star. Never give one contributor two stars. Never leave a contributor out. Every name in the input must appear in exactly one contributors array.'
    : 'There are only ' + numContributors + ' contributors but ' + starCount + ' stars, so a contributor may appear in more than one star. SPLIT the distinct ideas from each person across separate stars to reach ' + starCount + '. Every contributor must still appear at least once.';
  return 'You are the Constellation Brain for Yours Truly. Multiple people have described ONE person. Extract and organize their real words into a constellation of ' + starCount + ' stars.\nRULES:\n1. ' + coverageRule + '\n2. DISTILL each into a 1-2 word label. MAX 2 words, MAX 14 characters.\n3. Exactly 3 clusters with "name", "constellation_name" (single uppercase word), "constellation_title" ("THE ___" format).\n4. SCORE resonance 1-5.\n5. DECODE: Extract the BEST real quote from the contributor\'s actual memory text. Clean up spelling and grammar, but keep THEIR words — do NOT write new poetry. MAX 12 WORDS. The decode MUST come from the contributor\'s own words in "memory" field. You can tighten, trim filler, fix grammar — but the substance and vocabulary must be theirs.\n6. SUMMARY: one plaque-worthy sentence.\n7. HEADLINE: under 50 characters.\nCRITICAL: Total stars = ' + starCount + '. Labels 1-2 words lowercase. Decodes are REAL QUOTES from the contributor memories, cleaned up. JSON ONLY.\n{"clusters":[{"name":"The Quiet Architecture","constellation_name":"DRACO","constellation_title":"THE GUARDIAN","stars":[{"label":"quiet anchor","resonance":4,"contributors":["Diana"],"decode":"She stayed when no one else would."}]}],"summary":"...","headline":"..."}';
};

var PRODUCT_OPTIONS = [
  { key: "framed", label: "Framed", price: 79, blurb: "Premium framed print \u00B7 ships in 5-7 days" },
  { key: "poster", label: "Poster", price: 49, blurb: "Luster photo paper, rolled \u00B7 frame it yourself" },
];

var STYLES = [
  { id: "bloom", label: "Bloom", icon: "\u273F", desc: "Watercolor blush", bg: "linear-gradient(135deg, #F5E6EB, #E8D5E0)", light: true },
  { id: "starmap2", label: "Star Map", icon: "starmap_svg", desc: "Night sky drama", bg: "linear-gradient(135deg, #080818, #1A1040)" },
  { id: "starlight", label: "Starlight", icon: "\u2727", desc: "Platinum luxury", bg: "linear-gradient(135deg, #050510, #0A0A18)" },
  { id: "editorial", label: "Editorial", icon: "\u201C", desc: "One line, set huge", bg: "linear-gradient(135deg, #0D0D11, #16161C)" },
  { id: "overlap2", label: "Overlap", icon: "\u25D1", desc: "Luminous circles", bg: "linear-gradient(135deg, #0A0A0A, #151520)" },
  { id: "gallery2", label: "Gallery", icon: "\u25CE", desc: "Minimal luxury", bg: "linear-gradient(135deg, #0A0A0A, #101018)" },
  { id: "statement", label: "Statement", icon: "A", desc: "Bold typography", bg: "linear-gradient(135deg, #F2EDE4, #E8E0D4)", light: true },
  { id: "texts", label: "Texts", icon: "\uD83D\uDCAC", desc: "iMessage style", bg: "linear-gradient(135deg, #0F0F18, #1A1A2E)" },
  { id: "grid", label: "Grid", icon: "grid_svg", desc: "One cell per person", bg: "#F6F1E6", light: true },
  { id: "mosaic", label: "Mosaic", icon: "mosaic_svg", desc: "Words form the shape", bg: "radial-gradient(circle at 50% 45%, #3A2E14 0%, #080808 70%)" },
  { id: "custom", label: "Custom", icon: "custom_svg", desc: "Your design", bg: "linear-gradient(135deg, #050510, #C9A96E)" },
];

// Shared control styling for the design panel
var secLabel = function(t) {
  return { fontFamily: SANS, fontSize: 9, fontWeight: 300, color: t.accentSoft, letterSpacing: 4, textTransform: "uppercase", margin: "0 0 10px" };
};
var swatchPicker = function(t) {
  return { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center",
    border: "1px dashed " + t.border, cursor: "pointer", color: t.textMuted, fontSize: 14, position: "relative" };
};
var SliderRow = function(p) {
  return (
    <div style={{ marginBottom: 13 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
        <span style={{ fontFamily: SANS, fontSize: 10, color: p.t.textSoft, letterSpacing: 1.2 }}>{p.label}</span>
        <span style={{ fontFamily: SANS, fontSize: 9, color: p.t.textMuted, letterSpacing: 1 }}>{p.display}</span>
      </div>
      <input type="range" min={p.min} max={p.max} step={p.step} value={p.value}
        onChange={function(e) { p.onChange(parseFloat(e.target.value)); }}
        style={{ width: "100%", accentColor: "#C9A96E", cursor: "pointer" }} />
    </div>
  );
};
var chipStyle = function(t, on) {
  return {
    padding: "9px 16px", background: on ? t.accentMuted : "transparent",
    border: "1px solid " + (on ? t.accent : t.border), color: on ? t.text : t.textMuted,
    fontSize: 10, fontFamily: SANS, letterSpacing: 1.5, cursor: "pointer", transition: "all 0.25s",
  };
};

export default function ConstellationView() {
  var { mode, t, toggle } = useTheme();
  var [constellation, setConstellation] = useState(null);
  // ── Load persisted creator customizations from localStorage ──
  var storageKey = "yt_edits_" + slug;
  var saved = null;
  try { saved = JSON.parse(localStorage.getItem(storageKey)); } catch (e) {}

  var [contributions, setContributions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [step, setStep] = useState("idle");
  var [compiled, setCompiled] = useState(null);
  var [crystalData, setCrystalData] = useState(null);
  var [selectedStar, setSelectedStar] = useState(null);
  var [error, setError] = useState(null);
  var [copied, setCopied] = useState(false);
  var [crystalStyle, setCrystalStyle] = useState(saved && saved.style || "bloom");
  var [ordering, setOrdering] = useState(false);
  var [productKey, setProductKey] = useState("framed");
  var [voicesOpen, setVoicesOpen] = useState(false);
  var [editOpen, setEditOpen] = useState(false);
  var [layoutOpen, setLayoutOpen] = useState(false);
  var [layoutPreset, setLayoutPreset] = useState(saved && saved.layoutPreset || "scattered");
  var [customPositions, setCustomPositions] = useState(saved && saved.customPositions || {});
  var [customQuotePositions, setCustomQuotePositions] = useState(saved && saved.customQuotePositions || {});
  var [disabledVoices, setDisabledVoices] = useState(saved && saved.disabledVoices || {});
  var [editedQuotes, setEditedQuotes] = useState(saved && saved.editedQuotes || {});
  var [showQuotes, setShowQuotes] = useState(saved ? saved.showQuotes !== false : true);
  var [showAdjectives, setShowAdjectives] = useState(saved ? saved.showAdjectives !== false : true);
  var [showJokes, setShowJokes] = useState(saved ? saved.showJokes !== false : true);
  var [editedJokes, setEditedJokes] = useState(saved && saved.editedJokes || {});
  var [showLightbox, setShowLightbox] = useState(false);
  var [focusedStarIdx, setFocusedStarIdx] = useState(null);
  var [refreshing, setRefreshing] = useState(false);
  var [designOpen, setDesignOpen] = useState(false);
  var [customStyle, setCustomStyle] = useState(
    Object.assign({}, CUSTOM_DEFAULTS, (saved && saved.customStyle) || {})
  );
  var [mosaicStyle, setMosaicStyle] = useState(
    Object.assign({}, MOSAIC_DEFAULTS, (saved && saved.mosaicStyle) || {})
  );
  var [gridStyle, setGridStyle] = useState(
    Object.assign({}, GRID_DEFAULTS, (saved && saved.gridStyle) || {})
  );
  var [logoNote, setLogoNote] = useState(null);
  var [savedDesigns, setSavedDesigns] = useState([]);
  var [savedOpen, setSavedOpen] = useState(false);
  var [designName, setDesignName] = useState("");
  var [savingDesign, setSavingDesign] = useState(false);
  var [saveNote, setSaveNote] = useState(null);
  var compositorRef = useRef(null);
  var ordered = new URLSearchParams(window.location.search).get("ordered") === "true";

  // ── Auto-save customizations to localStorage ──
  useEffect(function() {
    if (!slug) return;
    var data = {
      style: crystalStyle,
      disabledVoices: disabledVoices,
      editedQuotes: editedQuotes,
      editedJokes: editedJokes,
      showQuotes: showQuotes,
      showAdjectives: showAdjectives,
      showJokes: showJokes,
      layoutPreset: layoutPreset,
      customPositions: customPositions,
      customQuotePositions: customQuotePositions,
      customStyle: customStyle,
      mosaicStyle: mosaicStyle,
      gridStyle: gridStyle,
    };
    try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch (e) {}
  }, [crystalStyle, disabledVoices, editedQuotes, editedJokes, showQuotes, showAdjectives, showJokes, layoutPreset, customPositions, customQuotePositions, customStyle, mosaicStyle, gridStyle]);

  var slug = new URLSearchParams(window.location.search).get("c");
  var isAdmin = new URLSearchParams(window.location.search).get("admin") === "true";

  // ── Export engraving file (admin only) ──
  var exportEngraving = function() {
    var svgEl = document.querySelector("[data-compositor] svg");
    if (!svgEl) return alert("Compose first, then export.");
    var clone = svgEl.cloneNode(true);
    clone.setAttribute("width", "2000");
    clone.setAttribute("height", "2000");
    var serializer = new XMLSerializer();
    var svgStr = serializer.serializeToString(clone);
    var blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    var url = URL.createObjectURL(blob);
    var img = new Image();
    img.onload = function() {
      var canvas = document.createElement("canvas");
      canvas.width = 2000; canvas.height = 2000;
      var ctx = canvas.getContext("2d");
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, 2000, 2000);
      ctx.drawImage(img, 0, 0, 2000, 2000);
      URL.revokeObjectURL(url);
      canvas.toBlob(function(pngBlob) {
        var a = document.createElement("a");
        a.href = URL.createObjectURL(pngBlob);
        a.download = (constellation ? constellation.recipient_name.replace(/\s+/g, "_") : "crystal") + "_engraving_" + crystalStyle + ".png";
        a.click();
      }, "image/png");
    };
    img.src = url;
  };

  // ── Export text file (admin only) ──
  var exportTextFile = function() {
    if (!crystalData || !compiled) return;
    var lines = [];
    lines.push("YOURS TRULY — Crystal Engraving Text");
    lines.push("=====================================");
    lines.push("");
    lines.push("Name: " + crystalData.personName);
    lines.push("Headline: " + crystalData.headline);
    lines.push("Summary: " + compiled.summary);
    lines.push("Style: " + crystalStyle);
    lines.push("");
    lines.push("--- QUOTES ---");
    crystalData.stars.forEach(function(s, i) {
      var contrib = s.starContributors && s.starContributors[0] ? s.starContributors[0] : "";
      lines.push((i + 1) + '. "' + (s.decode || s.label) + '" — ' + contrib);
    });
    lines.push("");
    lines.push("--- FONTS ---");
    lines.push("Name: Playfair Display, Regular 400 (Google Font)");
    lines.push("Quotes & Tagline: Cormorant Garamond, Light Italic 300 (Google Font)");
    lines.push("Attributions: Cormorant Garamond, Light 300 (Google Font)");
    lines.push("");
    lines.push("--- LAYOUT ---");
    lines.push("Monument: Name bottom-center, quotes symmetrical above.");
    lines.push("Voices: Quotes scattered, name bottom-center.");
    lines.push("");
    lines.push("--- NOTES ---");
    lines.push("The PNG engraving file is the exact layout.");
    lines.push("White = engrave. Black = clear glass.");
    var blob = new Blob([lines.join("\n")], { type: "text/plain" });
    var a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (crystalData.personName || "crystal").replace(/\s+/g, "_") + "_engraving_text.txt";
    a.click();
  };

  useEffect(function() {
    if (!slug) return;
    getConstellation(slug).then(function(c) {
      if (!c) { setLoading(false); return; }
      setConstellation(c);
      setSavedDesigns(Array.isArray(c.designs) ? c.designs : []);
      getContributions(c.id).then(function(ct) { setContributions(ct || []); setLoading(false); });
    });
  }, [slug]);

  // Refresh contributions
  var refreshVoices = async function() {
    if (!constellation || refreshing) return;
    setRefreshing(true);
    try {
      var ct = await getContributions(constellation.id);
      setContributions(ct || []);
    } catch(e) { console.error("Refresh failed:", e); }
    setRefreshing(false);
  };

  useEffect(function() {
    if (!showLightbox) return;
    var onKey = function(e) { if (e.key === "Escape") setShowLightbox(false); };
    window.addEventListener("keydown", onKey);
    var prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return function() {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showLightbox]);

  // ---- Saved designs (persisted to Supabase, survives refresh and other devices) ----
  var buildSnapshot = function(name) {
    var mos = Object.assign({}, mosaicStyle);
    delete mos.logoData; // data URI can be ~700KB; never persist it
    return {
      id: String(Date.now()), name: name, savedAt: new Date().toISOString(),
      style: crystalStyle, layoutPreset: layoutPreset,
      customPositions: customPositions, customQuotePositions: customQuotePositions,
      disabledVoices: disabledVoices, editedQuotes: editedQuotes, editedJokes: editedJokes,
      showQuotes: showQuotes, showAdjectives: showAdjectives, showJokes: showJokes,
      customStyle: customStyle, gridStyle: gridStyle, mosaicStyle: mos,
      hadLogo: !!mosaicStyle.logoData, compiled: compiled, crystalData: crystalData,
    };
  };

  var persistDesigns = async function(list) {
    if (!constellation) return false;
    try { return !!(await saveDesigns(constellation.id, list)); }
    catch (e) { console.error("saveDesigns failed:", e); return false; }
  };

  var saveCurrentDesign = async function() {
    if (!crystalData || savingDesign) return;
    setSavingDesign(true); setSaveNote(null);
    var label = designName.trim() || ((STYLES.filter(function(x) { return x.id === crystalStyle; })[0] || {}).label || crystalStyle);
    var list = savedDesigns.concat([buildSnapshot(label)]);
    var ok = await persistDesigns(list);
    if (ok) { setSavedDesigns(list); setDesignName(""); setSaveNote({ bad: false, text: "Saved \u2014 " + label }); }
    else { setSaveNote({ bad: true, text: "Couldn't save. Check the designs column exists on constellations." }); }
    setSavingDesign(false);
  };

  var loadDesign = function(d) {
    if (!d) return;
    if (d.compiled) setCompiled(d.compiled);
    if (d.crystalData) { setCrystalData(d.crystalData); setStep("done"); }
    setCrystalStyle(d.style || "bloom");
    setLayoutPreset(d.layoutPreset || "scattered");
    setCustomPositions(d.customPositions || {});
    setCustomQuotePositions(d.customQuotePositions || {});
    setDisabledVoices(d.disabledVoices || {});
    setEditedQuotes(d.editedQuotes || {});
    setEditedJokes(d.editedJokes || {});
    setShowQuotes(d.showQuotes !== false);
    setShowAdjectives(d.showAdjectives !== false);
    setShowJokes(d.showJokes !== false);
    setCustomStyle(Object.assign({}, CUSTOM_DEFAULTS, d.customStyle || {}));
    setGridStyle(Object.assign({}, GRID_DEFAULTS, d.gridStyle || {}));
    setMosaicStyle(Object.assign({}, MOSAIC_DEFAULTS, d.mosaicStyle || {}));
    setSaveNote(d.hadLogo ? { bad: true, text: "This design used an uploaded logo. Re-upload it under Design." } : null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  var deleteDesign = async function(id) {
    var list = savedDesigns.filter(function(d) { return d.id !== id; });
    if (await persistDesigns(list)) setSavedDesigns(list);
  };

  var runPipeline = async function() {
    if (!contributions.length) return;
    setError(null); setStep("compiling");
    try {
      var input = contributions.map(function(c) {
        // Extract memory text from transcript
        var userMsgs = (c.transcript || []).filter(function(m) { return m.role === "user" && m.content.indexOf("Inside joke:") !== 0; });
        var memoryText = userMsgs.map(function(m) { return m.content; }).join(" ");
        return { contributor: c.contributor_name, relationship: c.relationship, elements: c.elements || [], memory: memoryText };
      });
      var brainPrompt = BRAIN_TEMPLATE(contributions.length);
      var r = await fetch(API_URL, { method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY }, body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 3000, system: brainPrompt, messages: [{ role: "user", content: "Person: " + constellation.recipient_name + "\nOccasion: " + (constellation.occasion || "gift") + "\n\nContributions:\n" + JSON.stringify(input, null, 2) }] }) });
      var d = await r.json();
      var raw = (d.content || []).map(function(c) { return c.text || ""; }).join("").trim();
      var brainOutput = JSON.parse(raw.replace(/```json|```/g, "").trim());
      setCompiled(brainOutput);
      var allStars = [];
      brainOutput.clusters.forEach(function(cl) { cl.stars.forEach(function(star) { allStars.push({ label: star.label, resonance: star.resonance || 3, decode: star.decode || "", starContributors: star.contributors || [] }); }); });
      allStars.sort(function(a, b) { return b.resonance - a.resonance; });

      // Tally adjective frequency across all contributors
      var adjTally = {};
      contributions.forEach(function(c) {
        (c.elements || []).forEach(function(el) {
          var w = el.charAt(0).toUpperCase() + el.slice(1).toLowerCase();
          adjTally[w] = (adjTally[w] || 0) + 1;
        });
      });
      var adjectiveFreq = Object.keys(adjTally).map(function(word) {
        return { word: word, count: adjTally[word] };
      }).sort(function(a, b) { return b.count - a.count; });

      // Extract raw quotes (memory text from each contributor)
      var rawQuotes = contributions.map(function(c) {
        var userMsgs = (c.transcript || []).filter(function(m) { return m.role === "user" && m.content.indexOf("Inside joke:") !== 0; });
        return { text: userMsgs.map(function(m) { return m.content; }).join(" "), contributor: c.contributor_name };
      }).filter(function(q) { return q.text.trim(); });

      // Extract inside jokes
      var insideJokes = contributions.map(function(c) {
        var jokeMsgs = (c.transcript || []).filter(function(m) { return m.role === "user" && m.content.indexOf("Inside joke:") === 0; });
        return { text: jokeMsgs.map(function(m) { return m.content.replace("Inside joke: ", ""); }).join(" "), contributor: c.contributor_name };
      }).filter(function(j) { return j.text.trim(); });

      setCrystalData({
        personName: constellation.recipient_name,
        headline: brainOutput.headline || brainOutput.summary || "",
        summary: brainOutput.summary || "",
        stars: allStars,
        contributors: contributions.map(function(c) { return c.contributor_name; }),
        contributorMeta: contributions.map(function(c) {
          return { name: c.contributor_name, relationship: c.relationship || "" };
        }),
        adjectiveFreq: adjectiveFreq,
        rawQuotes: rawQuotes,
        insideJokes: insideJokes,
      });
    } catch (e) { console.error(e); setError("Compilation failed."); setStep("idle"); return; }
    setStep("done");
  };

  // F1: Filter crystal data based on disabled voices + apply edited quotes
  // -- Custom design helpers --
  var setCS = function(patch) {
    setCustomStyle(function(prev) { return Object.assign({}, prev, patch); });
  };
  var setGS = function(patch) {
    setGridStyle(function(prev) { return Object.assign({}, prev, patch); });
  };
  var setMS = function(patch) {
    setMosaicStyle(function(prev) { return Object.assign({}, prev, patch); });
  };
  // Logo goes into localStorage as a data URI, so it has to stay small or it
  // silently blows the quota and takes every other saved edit down with it.
  var handleLogoUpload = function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 700 * 1024) {
      setLogoNote({ bad: true, text: "That file is " + Math.round(file.size / 1024) + "KB. Keep it under 700KB \u2014 a flat PNG or SVG of the mark, not a photo." });
      e.target.value = "";
      return;
    }
    var reader = new FileReader();
    reader.onload = function() {
      setMS({ logoData: reader.result });
      setLogoNote({ bad: false, text: file.name + " loaded. If it reads as a solid blob, switch Dark/Light below." });
    };
    reader.onerror = function() { setLogoNote({ bad: true, text: "Couldn't read that file. Try a PNG or SVG." }); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  var gridInk = (!gridStyle.ink || gridStyle.ink === "auto") ? autoTextColor(gridStyle.ground) : gridStyle.ink;
  var gridContrast = contrastRatio(gridInk, gridStyle.ground);
  var gridTone = gridContrast >= 4.5 ? "#7FA87F" : gridContrast >= 3 ? "#C9A96E" : "#C87070";
  var gridNote = gridContrast >= 4.5
    ? "Reads cleanly \u2014 " + gridContrast.toFixed(1) + ":1"
    : gridContrast >= 3
      ? "Soft \u2014 small cell text may fade on matte paper"
      : "Too low \u2014 the cells won't be readable in print";
  var mosaicContrast = contrastRatio(mosaicStyle.ink, mosaicStyle.ground);
  var mosaicTone = mosaicContrast >= 4.5 ? "#7FA87F" : mosaicContrast >= 3 ? "#C9A96E" : "#C87070";
  var mosaicNote = mosaicContrast >= 4.5
    ? "Reads at a distance \u2014 " + mosaicContrast.toFixed(1) + ":1"
    : mosaicContrast >= 3
      ? "Soft \u2014 the mark will read, individual words may not"
      : "Too low \u2014 the words will disappear on paper";

  var surpriseDesign = function() {
    var pick = function(arr) { return arr[Math.floor(Math.random() * arr.length)]; };
    var bg = pick(CUSTOM_BACKGROUNDS).color;
    var legible = CUSTOM_TEXT_COLORS.filter(function(c) { return c.color && contrastRatio(c.color, bg) >= 4.5; });
    setCustomStyle({
      bg: bg,
      textColor: (legible.length && Math.random() > 0.35) ? pick(legible).color : "auto",
      finish: pick(CUSTOM_FINISHES).id,
      texture: pick(CUSTOM_TEXTURES).id,
      typeSet: pick(CUSTOM_TYPE_SETS).id,
      namePlacement: pick(CUSTOM_NAME_PLACEMENTS).id,
      quoteAlign: pick(CUSTOM_QUOTE_ALIGNS).id,
      starMark: pick(CUSTOM_STAR_MARKS).id,
      showLines: Math.random() > 0.25,
      border: pick(CUSTOM_BORDERS).id,
      nameCase: Math.random() > 0.6 ? "upper" : "asTyped",
      scale: 0.85 + Math.random() * 0.35,
      nameScale: 0.85 + Math.random() * 0.45,
      trackingScale: 0.5 + Math.random() * 1.6,
      quoteScale: 0.85 + Math.random() * 0.35,
    });
  };
  var resolvedTextColor = (!customStyle.textColor || customStyle.textColor === "auto")
    ? autoTextColor(customStyle.bg) : customStyle.textColor;
  var contrast = contrastRatio(resolvedTextColor, customStyle.bg);
  var contrastTone = contrast >= 4.5 ? "#7FA87F" : contrast >= 3 ? "#C9A96E" : "#C87070";
  var contrastNote = contrast >= 7
    ? "Prints cleanly \u2014 contrast " + contrast.toFixed(1) + ":1"
    : contrast >= 4.5
      ? "Good contrast \u2014 " + contrast.toFixed(1) + ":1"
      : contrast >= 3
        ? "Low contrast \u2014 thin text may fade on matte paper"
        : "Too low to print legibly \u2014 pick a lighter or darker text colour";

  var filteredCrystalData = crystalData ? (function() {
    var activeContribs = (crystalData.contributors || []).filter(function(name) { return !disabledVoices[name]; });
    var filteredStars = (crystalData.stars || []).filter(function(star) {
      return (star.starContributors || []).some(function(c) { return !disabledVoices[c]; });
    });
    // Apply edited quotes
    filteredStars = filteredStars.map(function(star, i) {
      var editKey = star.label + "_" + i;
      if (editedQuotes[editKey] !== undefined) {
        return Object.assign({}, star, { decode: editedQuotes[editKey] });
      }
      return star;
    });
    var filteredQuotes = (crystalData.rawQuotes || []).filter(function(q) { return !disabledVoices[q.contributor]; });
    var filteredJokes = (crystalData.insideJokes || []).filter(function(j) { return !disabledVoices[j.contributor]; });
    // Apply edited jokes
    filteredJokes = filteredJokes.map(function(j) {
      var jokeKey = j.contributor + "_joke";
      if (editedJokes[jokeKey] !== undefined) {
        return Object.assign({}, j, { text: editedJokes[jokeKey] });
      }
      return j;
    });
    // Retally adjectives from active contributions only
    var adjTally2 = {};
    contributions.filter(function(c) { return !disabledVoices[c.contributor_name]; }).forEach(function(c) {
      (c.elements || []).forEach(function(el) {
        var w = el.charAt(0).toUpperCase() + el.slice(1).toLowerCase();
        adjTally2[w] = (adjTally2[w] || 0) + 1;
      });
    });
    var filteredAdj = Object.keys(adjTally2).map(function(word) { return { word: word, count: adjTally2[word] }; }).sort(function(a, b) { return b.count - a.count; });
    return Object.assign({}, crystalData, {
      stars: showQuotes ? filteredStars : [],
      contributors: activeContribs,
      contributorMeta: (crystalData.contributorMeta || []).filter(function(m) { return !disabledVoices[m.name]; }),
      rawQuotes: showQuotes ? filteredQuotes : [],
      insideJokes: showJokes ? filteredJokes : [],
      adjectiveFreq: showAdjectives ? filteredAdj : [],
      layoutPreset: layoutPreset,
      customPositions: customPositions,
      customQuotePositions: customQuotePositions,
      customStyle: customStyle,
      mosaicStyle: mosaicStyle,
      gridStyle: gridStyle,
    });
  })() : null;

  var toggleVoice = function(name) {
    setDisabledVoices(function(prev) {
      var next = Object.assign({}, prev);
      if (next[name]) delete next[name];
      else next[name] = true;
      return next;
    });
  };

  var handleStarDrag = function(starIndex, pos) {
    setCustomPositions(function(prev) {
      var next = Object.assign({}, prev);
      next[starIndex] = pos;
      return next;
    });
  };

  var handleQuoteDrag = function(starIndex, pos) {
    setCustomQuotePositions(function(prev) {
      var next = Object.assign({}, prev);
      next[starIndex] = pos;
      return next;
    });
  };

  var allStars = compiled ? compiled.clusters.flatMap(function(cl) { return cl.stars.map(function(s) { return Object.assign({}, s, { clusterName: cl.name }); }); }) : [];
  var sel = selectedStar ? allStars.find(function(s) { return s.label.toLowerCase() === selectedStar.toLowerCase(); }) : null;
  var totalStars = compiled ? compiled.clusters.reduce(function(s, c) { return s + c.stars.length; }, 0) : 0;

  var copyLink = function() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.origin + "/contribute?c=" + slug);
      setCopied(true); setTimeout(function() { setCopied(false); }, 2000);
    }
  };

  var handleCheckout = async function() {
    if (!constellation || ordering) return;
    setOrdering(true);
    try {
      var imageUrl = "";
      if (compositorRef.current) {
        try {
          imageUrl = await captureAndUpload(compositorRef.current, constellation.id, crystalStyle, supabase);
        } catch (e) {
          console.error("Capture failed:", e);
          alert("Failed to capture poster image. Please try again. If the issue persists, try a different browser.");
          setOrdering(false);
          return;
        }
      }
      if (!imageUrl) {
        alert("No poster image captured. Please try again.");
        setOrdering(false);
        return;
      }
      var r = await fetch("/api/checkout", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ constellationId: constellation.id, style: crystalStyle, personName: constellation.recipient_name, slug: slug, imageUrl: imageUrl, product: productKey }) });
      var d = await r.json();
      if (d.url) window.location.href = d.url;
      else { console.error("Checkout error:", d.error); alert("Checkout failed. Please try again."); setOrdering(false); }
    } catch (e) { console.error("Checkout failed:", e); alert("Something went wrong. Please try again."); setOrdering(false); }
  };

  if (loading) return (
    <PageWrap t={t}>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: t.textMuted, fontFamily: SERIF, fontSize: 16, fontStyle: "italic" }}>loading&hellip;</p>
      </div>
    </PageWrap>
  );
  if (!constellation) return (
    <PageWrap t={t}>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: t.textMuted, fontFamily: SANS, fontSize: 14 }}>Constellation not found.</p>
      </div>
    </PageWrap>
  );

  return (
    <PageWrap t={t}>
      <style>{`
        @keyframes ytFadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes ytSlideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        @keyframes ytDot { 0%,100% { opacity:0.15; transform:scale(0.8); } 50% { opacity:0.8; transform:scale(1.2); } }
        @keyframes ytHighlight { 0%,100% { opacity: 0; } 50% { opacity: 1; } }
        .yt-pill { transition: all 0.3s ease !important; cursor: pointer !important; }
        .yt-pill:hover { border-color: ${t.accentSoft} !important; background: ${t.accentMuted} !important; }
        .yt-cta { transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94) !important; cursor: pointer !important; }
        .yt-cta:hover { transform: translateY(-2px) !important; letter-spacing: 3px !important; }
      `}</style>
      <ThemeToggle t={t} toggle={toggle} mode={mode} />

      {/* ── HEADER ── */}
      <div style={{ textAlign: "center", padding: "100px 24px 16px", animation: "ytFadeIn 0.8s ease-out both" }}>
        <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, color: t.accentSoft, letterSpacing: 8, textTransform: "uppercase", marginBottom: 24 }}>Yours Truly</p>
        <h1 style={{ fontFamily: PLAY, fontStyle: "italic", fontWeight: 400, fontSize: "clamp(36px, 8vw, 56px)", color: t.text, margin: 0, letterSpacing: 2, lineHeight: 1.1 }}>{constellation.recipient_name}</h1>
        {constellation.occasion && <p style={{ fontFamily: SANS, fontSize: 10, fontWeight: 300, color: t.accentSoft, marginTop: 20, letterSpacing: 5, textTransform: "uppercase" }}>{constellation.occasion}</p>}
        <p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 300, color: t.textMuted, marginTop: 10, letterSpacing: 2 }}>{contributions.length} voice{contributions.length !== 1 ? "s" : ""}{compiled ? " \u00B7 " + totalStars + " stars" : ""}</p>
      </div>

      {/* ── SUMMARY QUOTE ── */}
      {compiled && compiled.summary && (
        <div style={{ textAlign: "center", padding: "28px 24px 48px", animation: "ytFadeIn 0.8s ease-out both", animationDelay: "0.15s" }}>
          <div style={{ maxWidth: 480, margin: "0 auto", position: "relative" }}>
            <span style={{ fontFamily: SERIF, fontSize: 48, color: t.accentMuted, position: "absolute", top: -20, left: -4, lineHeight: 1, userSelect: "none" }}>&ldquo;</span>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: "clamp(15px, 3.5vw, 19px)", fontWeight: 300, color: t.textSoft, lineHeight: 2, margin: 0, padding: "0 16px" }}>{compiled.summary}</p>
            <span style={{ fontFamily: SERIF, fontSize: 48, color: t.accentMuted, position: "absolute", bottom: -36, right: -4, lineHeight: 1, userSelect: "none" }}>&rdquo;</span>
          </div>
        </div>
      )}

      {/* ── COMPOSE BUTTON + REFRESH ── */}
      {step === "idle" && contributions.length > 0 && (
        <div style={{ textAlign: "center", padding: "36px 0 52px", animation: "ytFadeIn 0.6s ease-out both" }}>
          <button onClick={runPipeline} className="yt-cta" style={{ ...btnStyle(t), padding: "18px 56px", fontSize: 12 }}>
            Compose {contributions.length} voice{contributions.length !== 1 ? "s" : ""}
          </button>
          <div style={{ marginTop: 16 }}>
            <button onClick={refreshVoices} disabled={refreshing} style={{
              padding: "8px 20px", background: "transparent", border: "1px solid " + t.border,
              color: t.textMuted, fontSize: 9, fontFamily: SANS, letterSpacing: 2, textTransform: "uppercase",
              cursor: refreshing ? "wait" : "pointer", opacity: refreshing ? 0.5 : 1,
            }}>{refreshing ? "Checking\u2026" : "\u21BB  Refresh voices"}</button>
          </div>
        </div>
      )}

      {/* ── COMPILING ── */}
      {step === "compiling" && (
        <div style={{ textAlign: "center", padding: "64px 0 80px" }}>
          <div style={{ marginBottom: 28 }}>
            {[0, 1, 2, 3, 4, 5].map(function(i) {
              return <span key={i} style={{ display: "inline-block", width: 4, height: 4, borderRadius: "50%", background: t.accent, margin: "0 5px", animation: "ytDot 1.6s ease-in-out infinite", animationDelay: i * 0.15 + "s" }} />;
            })}
          </div>
          <p style={{ color: t.textSoft, fontFamily: SERIF, fontStyle: "italic", fontSize: 17, margin: 0 }}>reading the stars&hellip;</p>
        </div>
      )}

      {/* ── ERROR ── */}
      {error && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <p style={{ color: "#C87070", fontFamily: SANS, fontSize: 13 }}>{error}</p>
          <button onClick={function() { setError(null); setStep("idle"); }} style={{ marginTop: 12, padding: "12px 28px", background: "transparent", border: "1px solid " + t.border, color: t.textMuted, fontSize: 11, cursor: "pointer", fontFamily: SANS, letterSpacing: 2, textTransform: "uppercase" }}>Try again</button>
        </div>
      )}

      {/* ── FRAMED POSTER DISPLAY ── */}
      {step === "done" && crystalData && (
        <div ref={compositorRef} data-compositor="true" style={{ padding: "16px 24px 0", animation: "ytSlideUp 0.8s ease-out both" }}>
          {/* Wall shadow — clickable to zoom */}
          <div onClick={function() { setShowLightbox(false); }} style={{ maxWidth: 520, margin: "0 auto", boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 6px 20px rgba(0,0,0,0.3)" }}>
            {/* Frame outer edge */}
            <div style={{ background: "linear-gradient(145deg, #2A2A2A 0%, #1A1A1A 50%, #0E0E0E 100%)", padding: 2, boxShadow: "inset 1px 1px 0 rgba(255,255,255,0.06), inset -1px -1px 0 rgba(0,0,0,0.4)" }}>
              {/* Frame body — slim black */}
              <div style={{ background: "linear-gradient(180deg, #1C1C1C 0%, #141414 40%, #0F0F0F 100%)", padding: 10, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), inset 0 -1px 0 rgba(0,0,0,0.5)" }}>
                {/* Inner bevel */}
                <div style={{ boxShadow: "inset 1px 1px 2px rgba(0,0,0,0.6), inset -1px -1px 1px rgba(255,255,255,0.02)", background: "#0A0A0A", padding: 1 }}>
                  <CrystalCompositor data={filteredCrystalData} crystalStyle={crystalStyle} highlightIndex={focusedStarIdx} onStarDrag={layoutOpen ? handleStarDrag : null} onQuoteDrag={layoutOpen ? handleQuoteDrag : null} />
                </div>
              </div>
            </div>
          </div>
          <p style={{ textAlign: "center", fontFamily: SANS, fontSize: 9, color: t.textMuted, marginTop: 10, letterSpacing: 2 }}>&nbsp;</p>
        </div>
      )}

      {/* ── INLINE CONTROL BAR — Voices + Edit Quotes ── */}
      {step === "done" && crystalData && (
        <div style={{ maxWidth: 560, margin: "24px auto 0", padding: "0 16px", animation: "ytFadeIn 0.5s ease-out both", animationDelay: "0.2s" }}>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={function() { setVoicesOpen(!voicesOpen); setEditOpen(false); setLayoutOpen(false); setDesignOpen(false); setSavedOpen(false); }} className="yt-pill" style={{
              padding: "10px 28px", background: voicesOpen ? t.accentMuted : t.cardBg,
              border: "1px solid " + (voicesOpen ? t.accent : t.border), color: voicesOpen ? t.text : t.textSoft,
              fontSize: 10, fontFamily: SANS, fontWeight: voicesOpen ? 500 : 300, letterSpacing: 3, textTransform: "uppercase",
              cursor: "pointer",
            }}>Voices</button>
            <button onClick={function() { setEditOpen(!editOpen); setVoicesOpen(false); setLayoutOpen(false); setDesignOpen(false); setSavedOpen(false); }} className="yt-pill" style={{
              padding: "10px 28px", background: editOpen ? t.accentMuted : t.cardBg,
              border: "1px solid " + (editOpen ? t.accent : t.border), color: editOpen ? t.text : t.textSoft,
              fontSize: 10, fontFamily: SANS, fontWeight: editOpen ? 500 : 300, letterSpacing: 3, textTransform: "uppercase",
              cursor: "pointer",
            }}>Edit</button>
            {(crystalStyle === "custom" || crystalStyle === "mosaic" || crystalStyle === "grid") && (
              <button onClick={function() { setDesignOpen(!designOpen); setVoicesOpen(false); setEditOpen(false); setLayoutOpen(false); setSavedOpen(false); }} className="yt-pill" style={{
                padding: "10px 28px", background: designOpen ? t.accentMuted : t.cardBg,
                border: "1px solid " + (designOpen ? t.accent : t.border), color: designOpen ? t.text : t.textSoft,
                fontSize: 10, fontFamily: SANS, fontWeight: designOpen ? 500 : 300, letterSpacing: 3, textTransform: "uppercase",
                cursor: "pointer",
              }}>Design</button>
            )}
            {(crystalStyle === "starmap2" || crystalStyle === "starlight" || crystalStyle === "custom") && (
              <button onClick={function() { setLayoutOpen(!layoutOpen); setVoicesOpen(false); setEditOpen(false); setDesignOpen(false); setSavedOpen(false); }} className="yt-pill" style={{
                padding: "10px 28px", background: layoutOpen ? t.accentMuted : t.cardBg,
                border: "1px solid " + (layoutOpen ? t.accent : t.border), color: layoutOpen ? t.text : t.textSoft,
                fontSize: 10, fontFamily: SANS, fontWeight: layoutOpen ? 500 : 300, letterSpacing: 3, textTransform: "uppercase",
                cursor: "pointer",
              }}>Layout</button>
            )}
            <button onClick={function() { setSavedOpen(!savedOpen); setVoicesOpen(false); setEditOpen(false); setDesignOpen(false); setLayoutOpen(false); }} className="yt-pill" style={{
              padding: "10px 28px", background: savedOpen ? t.accentMuted : t.cardBg,
              border: "1px solid " + (savedOpen ? t.accent : t.border), color: savedOpen ? t.text : t.textSoft,
              fontSize: 10, fontFamily: SANS, fontWeight: savedOpen ? 500 : 300, letterSpacing: 3, textTransform: "uppercase",
              cursor: "pointer",
            }}>Saved{savedDesigns.length ? " (" + savedDesigns.length + ")" : ""}</button>
            <button onClick={function() { setShowLightbox(true); }} className="yt-pill" style={{
              padding: "10px 28px", background: t.cardBg,
              border: "1px solid " + t.border, color: t.textSoft,
              fontSize: 10, fontFamily: SANS, fontWeight: 300, letterSpacing: 3, textTransform: "uppercase",
              cursor: "pointer",
            }}>Fullscreen</button>
          </div>

          {/* ── CONTENT TOGGLES ── */}
          <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 16 }}>
            {[
              { label: "Quotes", on: showQuotes, toggle: function() { setShowQuotes(!showQuotes); } },
              { label: "Adjectives", on: showAdjectives, toggle: function() { setShowAdjectives(!showAdjectives); } },
              { label: "Inside Jokes", on: showJokes, toggle: function() { setShowJokes(!showJokes); } },
            ].map(function(item) {
              return (
                <button key={item.label} onClick={item.toggle} style={{
                  padding: "6px 16px", background: "transparent",
                  border: "1px solid " + (item.on ? t.accent : t.border),
                  color: item.on ? t.text : t.textMuted,
                  fontSize: 9, fontFamily: SANS, fontWeight: item.on ? 400 : 300,
                  letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                  opacity: item.on ? 1 : 0.5, transition: "all 0.3s",
                }}>{item.on ? "\u2713 " : ""}{item.label}</button>
              );
            })}
          </div>

          {/* ── SAVED DESIGNS ACCORDION ── */}
          {savedOpen && (
            <div style={{ marginTop: 20, padding: "24px", background: t.cardBg, border: "1px solid " + t.border, animation: "ytFadeIn 0.3s ease-out both" }}>
              <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, color: t.accentSoft, letterSpacing: 4, textTransform: "uppercase", margin: "0 0 14px" }}>Save this design</p>
              <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                <input className="yt-input" placeholder="Name it \u2014 e.g. Starlight gold" value={designName}
                  onChange={function(e) { setDesignName(e.target.value); }}
                  onKeyDown={function(e) { if (e.key === "Enter") saveCurrentDesign(); }}
                  style={{ flex: 1, padding: "10px 14px", background: "transparent", border: "1px solid " + t.border, color: t.text, fontFamily: SANS, fontSize: 13, outline: "none" }} />
                <button onClick={saveCurrentDesign} disabled={savingDesign} style={{
                  padding: "10px 22px", background: t.accentMuted, border: "1px solid " + t.accent, color: t.text,
                  fontSize: 10, fontFamily: SANS, letterSpacing: 2, textTransform: "uppercase",
                  cursor: savingDesign ? "wait" : "pointer", opacity: savingDesign ? 0.5 : 1,
                }}>{savingDesign ? "Saving" : "Save"}</button>
              </div>
              {saveNote && (<p style={{ fontFamily: SANS, fontSize: 11, fontWeight: 300, color: saveNote.bad ? "#C87070" : t.accentSoft, margin: "0 0 12px" }}>{saveNote.text}</p>)}
              {savedDesigns.length === 0 && (
                <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 13, color: t.textMuted, margin: "16px 0 0" }}>No saved designs yet. Save a few, then send the link and let people pick.</p>
              )}
              {savedDesigns.map(function(d) {
                var active = d.style === crystalStyle;
                return (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: "1px solid " + t.border }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: SANS, fontSize: 13, fontWeight: 400, color: t.text, margin: 0 }}>{d.name}</p>
                      <p style={{ fontFamily: SANS, fontSize: 10, fontWeight: 300, color: t.textMuted, margin: "3px 0 0", letterSpacing: 1 }}>
                        {((STYLES.filter(function(x) { return x.id === d.style; })[0] || {}).label || d.style)}
                        {d.savedAt ? " \u00B7 " + new Date(d.savedAt).toLocaleDateString() : ""}
                        {active ? " \u00B7 current style" : ""}
                      </p>
                    </div>
                    <button onClick={function() { loadDesign(d); }} style={{
                      padding: "8px 18px", background: "transparent", border: "1px solid " + t.accent, color: t.text,
                      fontSize: 9, fontFamily: SANS, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                    }}>Load</button>
                    <button onClick={function() { deleteDesign(d.id); }} style={{
                      padding: "8px 12px", background: "transparent", border: "1px solid " + t.border, color: t.textMuted,
                      fontSize: 9, fontFamily: SANS, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                    }}>Delete</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── FULLSCREEN OVERLAY ── */}
      {showLightbox && crystalData && createPortal(
        <div onClick={function() { setShowLightbox(false); }} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999,
          background: "rgba(6,6,10,0.96)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "3vmin", cursor: "zoom-out",
        }}>
          <button onClick={function(e) { e.stopPropagation(); setShowLightbox(false); }} aria-label="Close fullscreen" style={{
            position: "absolute", top: 20, right: 22, background: "transparent",
            border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.75)",
            width: 38, height: 38, fontSize: 16, fontFamily: SANS, cursor: "pointer", lineHeight: 1,
          }}>{"\u00D7"}</button>
          <div onClick={function(e) { e.stopPropagation(); }} style={{ width: "min(94vw, 94vh)", maxWidth: 1100, cursor: "default" }}>
            <div style={{ background: "linear-gradient(145deg, #2A2A2A 0%, #1A1A1A 50%, #0E0E0E 100%)", padding: 2 }}>
              <div style={{ background: "linear-gradient(180deg, #1C1C1C 0%, #141414 40%, #0F0F0F 100%)", padding: "1.6vmin" }}>
                <div style={{ background: "#0A0A0A", padding: 1 }}>
                  <CrystalCompositor data={filteredCrystalData} crystalStyle={crystalStyle} highlightIndex={null} onStarDrag={null} onQuoteDrag={null} />
                </div>
              </div>
            </div>
          </div>
          <p style={{
            position: "absolute", bottom: 18, left: 0, right: 0, textAlign: "center",
            fontFamily: SANS, fontSize: 10, fontWeight: 300, letterSpacing: 3,
            color: "rgba(255,255,255,0.35)", textTransform: "uppercase", margin: 0,
          }}>Tap anywhere or press Esc to close</p>
        </div>,
        document.body
      )}

      {/* ── VOICES ACCORDION ── */}
          {voicesOpen && (
            <div style={{ marginTop: 20, padding: "24px", background: t.cardBg, border: "1px solid " + t.border, animation: "ytFadeIn 0.3s ease-out both" }}>
              {(crystalData.contributors || []).map(function(name) {
                var isOn = !disabledVoices[name];
                return (
                  <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid " + t.border }}>
                    <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: isOn ? 400 : 300, color: isOn ? t.text : t.textMuted, transition: "all 0.3s" }}>{name}</span>
                    <button onClick={function() { toggleVoice(name); }} style={{
                      width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                      background: isOn ? t.accent : (mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                      position: "relative", transition: "background 0.3s",
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 8,
                        background: isOn ? "#fff" : (mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)"),
                        position: "absolute", top: 3, left: isOn ? 21 : 3,
                        transition: "left 0.3s, background 0.3s",
                      }} />
                    </button>
                  </div>
                );
              })}
              <p style={{ fontFamily: SANS, fontSize: 10, color: t.textMuted, marginTop: 12, marginBottom: 0 }}>
                {Object.keys(disabledVoices).length > 0 ? (crystalData.contributors.length - Object.keys(disabledVoices).length) + " of " + crystalData.contributors.length + " active" : "All voices active"}
              </p>
            </div>
          )}

          {/* ── EDIT BY PERSON ACCORDION ── */}
          {editOpen && filteredCrystalData && (
            <div style={{ marginTop: 20, animation: "ytFadeIn 0.3s ease-out both" }}>
              {(filteredCrystalData.contributors || []).map(function(contribName) {
                // Find this person's stars
                var personStars = (filteredCrystalData.stars || []).filter(function(star) {
                  return (star.starContributors || []).indexOf(contribName) !== -1;
                });
                // Find this person's inside jokes
                var personJokes = (filteredCrystalData.insideJokes || []).filter(function(j) {
                  return j.contributor === contribName;
                });
                if (personStars.length === 0 && personJokes.length === 0) return null;
                return (
                  <div key={contribName} style={{ marginBottom: 20, padding: "18px", background: t.cardBg, border: "1px solid " + t.border }}>
                    <p style={{ fontFamily: SANS, fontSize: 12, fontWeight: 500, color: t.text, letterSpacing: 2, textTransform: "uppercase", marginTop: 0, marginBottom: 16 }}>{contribName}</p>

                    {personStars.map(function(star, si) {
                      var quote = star.decode || star.label;
                      var globalIdx = (filteredCrystalData.stars || []).indexOf(star);
                      var editKey = star.label + "_" + globalIdx;
                      return (
                        <div key={editKey} style={{ marginBottom: 12 }}>
                          <span style={{ fontFamily: SANS, fontSize: 9, color: t.accentSoft, letterSpacing: 1, textTransform: "uppercase" }}>{star.label}</span>
                          <textarea value={editedQuotes[editKey] !== undefined ? editedQuotes[editKey] : quote} onChange={function(e) {
                            setEditedQuotes(function(prev) { var next = Object.assign({}, prev); next[editKey] = e.target.value; return next; });
                          }} onFocus={function() { setFocusedStarIdx(globalIdx); }} onBlur={function() { setFocusedStarIdx(null); }} style={{
                            width: "100%", minHeight: 40, padding: "8px 10px", border: "1px solid " + t.border,
                            background: "transparent", color: t.text, fontFamily: SERIF, fontSize: 13,
                            fontStyle: "italic", fontWeight: 300, lineHeight: 1.7, resize: "vertical",
                            outline: "none", boxSizing: "border-box", marginTop: 4,
                          }} />
                          {editedQuotes[editKey] !== undefined && editedQuotes[editKey] !== quote && (
                            <button onClick={function() {
                              setEditedQuotes(function(prev) { var next = Object.assign({}, prev); delete next[editKey]; return next; });
                            }} style={{
                              marginTop: 4, padding: "3px 8px", background: "transparent",
                              border: "1px solid " + t.border, color: t.textMuted, fontSize: 8,
                              fontFamily: SANS, cursor: "pointer", letterSpacing: 1,
                            }}>Reset</button>
                          )}
                        </div>
                      );
                    })}

                    {personJokes.map(function(joke, ji) {
                      var jokeKey = contribName + "_joke";
                      var jokeText = editedJokes[jokeKey] !== undefined ? editedJokes[jokeKey] : joke.text;
                      return (
                        <div key={jokeKey + ji} style={{ marginTop: personStars.length > 0 ? 8 : 0 }}>
                          <span style={{ fontFamily: SANS, fontSize: 9, color: "rgba(120,160,130,0.8)", letterSpacing: 1, textTransform: "uppercase" }}>Inside Joke</span>
                          <textarea value={jokeText} onChange={function(e) {
                            setEditedJokes(function(prev) { var next = Object.assign({}, prev); next[jokeKey] = e.target.value; return next; });
                          }} style={{
                            width: "100%", minHeight: 36, padding: "8px 10px", border: "1px solid " + t.border,
                            background: "transparent", color: t.text, fontFamily: SANS, fontSize: 12,
                            fontWeight: 300, lineHeight: 1.6, resize: "vertical",
                            outline: "none", boxSizing: "border-box", marginTop: 4,
                          }} />
                          {editedJokes[jokeKey] !== undefined && editedJokes[jokeKey] !== joke.text && (
                            <button onClick={function() {
                              setEditedJokes(function(prev) { var next = Object.assign({}, prev); delete next[jokeKey]; return next; });
                            }} style={{
                              marginTop: 4, padding: "3px 8px", background: "transparent",
                              border: "1px solid " + t.border, color: t.textMuted, fontSize: 8,
                              fontFamily: SANS, cursor: "pointer", letterSpacing: 1,
                            }}>Reset</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── LAYOUT ACCORDION ── */}
          {layoutOpen && (crystalStyle === "starmap2" || crystalStyle === "starlight" || crystalStyle === "custom") && (
            <div style={{ marginTop: 20, padding: "24px", background: t.cardBg, border: "1px solid " + t.border, animation: "ytFadeIn 0.3s ease-out both" }}>
              <p style={{ fontFamily: SANS, fontSize: 10, color: t.textMuted, margin: "0 0 16px", lineHeight: 1.6 }}>Pick a shape, then drag individual stars to fine-tune.</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                {LAYOUT_PRESET_NAMES.map(function(p) {
                  var isActive = layoutPreset === p.id;
                  return (
                    <button key={p.id} onClick={function() { setLayoutPreset(p.id); setCustomPositions({}); }} style={{
                      padding: "10px 16px", background: isActive ? t.accentMuted : "transparent",
                      border: "1px solid " + (isActive ? t.accent : t.border),
                      color: isActive ? t.text : t.textMuted,
                      fontSize: 11, fontFamily: SANS, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                      transition: "all 0.3s",
                    }}>
                      <span style={{ fontSize: 16 }}>{p.icon}</span>
                      <span style={{ letterSpacing: 1 }}>{p.label}</span>
                    </button>
                  );
                })}
              </div>
              {(Object.keys(customPositions).length > 0 || Object.keys(customQuotePositions).length > 0) && (
                <button onClick={function() { setCustomPositions({}); setCustomQuotePositions({}); }} style={{
                  padding: "6px 16px", background: "transparent",
                  border: "1px solid " + t.border, color: t.textMuted,
                  fontSize: 9, fontFamily: SANS, cursor: "pointer", letterSpacing: 2,
                }}>Reset positions</button>
              )}
            </div>
          )}


          {/* ── DESIGN ACCORDION (Custom style) ── */}
          {designOpen && crystalStyle === "custom" && (
            <div style={{ marginTop: 20, padding: "24px", background: t.cardBg, border: "1px solid " + t.border, animation: "ytFadeIn 0.3s ease-out both" }}>

              {/* Background */}
              <p style={secLabel(t)}>Background</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {CUSTOM_BACKGROUNDS.map(function(b) {
                  var on = customStyle.bg === b.color;
                  return (
                    <button key={b.id} title={b.label} onClick={function() { setCS({ bg: b.color }); }} style={{
                      width: 34, height: 34, background: b.color, cursor: "pointer",
                      border: on ? "2px solid " + t.accent : "1px solid rgba(140,140,140,0.35)",
                      boxShadow: on ? "0 0 10px rgba(201,169,110,0.4)" : "none",
                      transform: on ? "scale(1.1)" : "scale(1)", transition: "all 0.2s",
                    }} />
                  );
                })}
                <label style={swatchPicker(t)}>
                  +
                  <input type="color" value={customStyle.bg} onChange={function(e) { setCS({ bg: e.target.value }); }}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
                </label>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_FINISHES.map(function(f) {
                  return <button key={f.id} onClick={function() { setCS({ finish: f.id }); }} style={chipStyle(t, customStyle.finish === f.id)}>{f.label}</button>;
                })}
              </div>

              {/* Text colour */}
              <p style={secLabel(t)}>Text colour</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {CUSTOM_TEXT_COLORS.map(function(c) {
                  var on = (customStyle.textColor || "auto") === (c.color || "auto");
                  return (
                    <button key={c.id} title={c.label} onClick={function() { setCS({ textColor: c.color || "auto" }); }} style={{
                      width: 34, height: 34, cursor: "pointer",
                      background: c.color || "linear-gradient(135deg, #FFF 0 50%, #141014 50% 100%)",
                      border: on ? "2px solid " + t.accent : "1px solid rgba(140,140,140,0.35)",
                      boxShadow: on ? "0 0 10px rgba(201,169,110,0.4)" : "none",
                      transform: on ? "scale(1.1)" : "scale(1)", transition: "all 0.2s",
                    }} />
                  );
                })}
                <label style={swatchPicker(t)}>
                  +
                  <input type="color" value={resolvedTextColor} onChange={function(e) { setCS({ textColor: e.target.value }); }}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
                </label>
              </div>

              {/* Contrast meter */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ height: 3, background: "rgba(140,140,140,0.2)", marginBottom: 6 }}>
                  <div style={{ height: 3, width: Math.min(100, (contrast / 12) * 100) + "%", background: contrastTone, transition: "all 0.3s" }} />
                </div>
                <p style={{ fontFamily: SANS, fontSize: 9, color: contrastTone, margin: 0, letterSpacing: 1 }}>{contrastNote}</p>
              </div>

              <p style={secLabel(t)}>Texture</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_TEXTURES.map(function(x) {
                  return <button key={x.id} title={x.hint} onClick={function() { setCS({ texture: x.id }); }} style={chipStyle(t, customStyle.texture === x.id)}>{x.label}</button>;
                })}
              </div>

              {/* Type */}
              <p style={secLabel(t)}>Typeface</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_TYPE_SETS.map(function(ts) {
                  return <button key={ts.id} title={ts.hint} onClick={function() { setCS({ typeSet: ts.id }); }} style={chipStyle(t, customStyle.typeSet === ts.id)}>{ts.label}</button>;
                })}
              </div>

              {/* Size */}
              <p style={secLabel(t)}>Size</p>
              <SliderRow t={t} label="Name" value={customStyle.nameScale} min={0.6} max={1.6} step={0.05}
                display={Math.round(customStyle.nameScale * 100) + "%"} onChange={function(v) { setCS({ nameScale: v }); }} />
              <SliderRow t={t} label="Name letter spacing" value={customStyle.trackingScale} min={0} max={3} step={0.1}
                display={customStyle.trackingScale.toFixed(1) + "\u00D7"} onChange={function(v) { setCS({ trackingScale: v }); }} />
              <SliderRow t={t} label="Quotes" value={customStyle.quoteScale} min={0.7} max={1.5} step={0.05}
                display={Math.round(customStyle.quoteScale * 100) + "%"} onChange={function(v) { setCS({ quoteScale: v }); }} />
              <SliderRow t={t} label="Constellation" value={customStyle.scale} min={0.7} max={1.3} step={0.05}
                display={Math.round(customStyle.scale * 100) + "%"} onChange={function(v) { setCS({ scale: v }); }} />
              <p style={{ fontFamily: SANS, fontSize: 9, color: t.textMuted, margin: "0 0 22px", lineHeight: 1.6 }}>
                Quotes shrink automatically if they'd run past the edge. Nothing gets cut.
              </p>

              {/* Name */}
              <p style={secLabel(t)}>Name</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {CUSTOM_NAME_PLACEMENTS.map(function(p) {
                  return <button key={p.id} onClick={function() { setCS({ namePlacement: p.id }); }} style={chipStyle(t, customStyle.namePlacement === p.id)}>{p.label}</button>;
                })}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_NAME_CASES.map(function(c) {
                  return <button key={c.id} onClick={function() { setCS({ nameCase: c.id }); }} style={chipStyle(t, customStyle.nameCase === c.id)}>{c.label}</button>;
                })}
              </div>

              {/* Quote alignment */}
              <p style={secLabel(t)}>Quote alignment</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_QUOTE_ALIGNS.map(function(q) {
                  return <button key={q.id} title={q.hint} onClick={function() { setCS({ quoteAlign: q.id }); }} style={chipStyle(t, customStyle.quoteAlign === q.id)}>{q.label}</button>;
                })}
              </div>

              {/* Stars */}
              <p style={secLabel(t)}>Stars</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {CUSTOM_STAR_MARKS.map(function(m) {
                  return (
                    <button key={m.id} onClick={function() { setCS({ starMark: m.id }); }} style={Object.assign({}, chipStyle(t, customStyle.starMark === m.id), { display: "flex", alignItems: "center", gap: 7 })}>
                      <span style={{ fontSize: 12 }}>{m.icon}</span>{m.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                <button onClick={function() { setCS({ showLines: true }); }} style={chipStyle(t, customStyle.showLines !== false)}>Connect the stars</button>
                <button onClick={function() { setCS({ showLines: false }); }} style={chipStyle(t, customStyle.showLines === false)}>No lines</button>
              </div>

              {/* Frame */}
              <p style={secLabel(t)}>Frame rule</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_BORDERS.map(function(b) {
                  return <button key={b.id} onClick={function() { setCS({ border: b.id }); }} style={chipStyle(t, customStyle.border === b.id)}>{b.label}</button>;
                })}
              </div>

              <p style={{ fontFamily: SANS, fontSize: 10, color: t.textMuted, margin: "0 0 14px", lineHeight: 1.6 }}>
                Constellation shape and individual star positions live under Layout.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button onClick={surpriseDesign} style={{
                  padding: "8px 18px", background: t.accentMuted, border: "1px solid " + t.accent,
                  color: t.text, fontSize: 9, fontFamily: SANS, cursor: "pointer", letterSpacing: 2, textTransform: "uppercase",
                }}>Surprise me</button>
                <button onClick={function() { setCustomStyle(Object.assign({}, CUSTOM_DEFAULTS)); }} style={{
                  padding: "8px 18px", background: "transparent", border: "1px solid " + t.border,
                  color: t.textMuted, fontSize: 9, fontFamily: SANS, cursor: "pointer", letterSpacing: 2, textTransform: "uppercase",
                }}>Reset design</button>
              </div>
            </div>
          )}

          {/* ── MOSAIC ACCORDION ── */}
          {designOpen && crystalStyle === "mosaic" && (
            <div style={{ marginTop: 20, padding: "24px", background: t.cardBg, border: "1px solid " + t.border, animation: "ytFadeIn 0.3s ease-out both" }}>

              <p style={secLabel(t)}>Shape</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                {MOSAIC_SHAPES.map(function(s) {
                  return <button key={s.id} title={s.hint} onClick={function() { setMS({ shape: s.id }); }} style={chipStyle(t, mosaicStyle.shape === s.id)}>{s.label}</button>;
                })}
              </div>

              {mosaicStyle.shape === "initials" && (
                <div style={{ marginBottom: 22 }}>
                  <input
                    type="text"
                    value={mosaicStyle.shapeText || ""}
                    maxLength={4}
                    placeholder={"Auto \u2014 " + ((constellation && constellation.recipient_name) || "").trim().split(/\s+/).filter(Boolean).slice(0, 3).map(function(p) { return p.charAt(0).toUpperCase(); }).join("")}
                    onChange={function(e) { setMS({ shapeText: e.target.value.toUpperCase() }); }}
                    style={{
                      width: "100%", padding: "12px 14px", background: "transparent",
                      border: "1px solid " + t.border, color: t.text, fontSize: 16,
                      fontFamily: PLAY, letterSpacing: 4, textAlign: "center",
                    }}
                  />
                  <p style={{ fontFamily: SANS, fontSize: 9, color: t.textMuted, margin: "8px 0 0", lineHeight: 1.6 }}>
                    Leave blank to use their initials. One to three letters fills the plate best &mdash; longer words flatten into a thin band with little room for words inside.
                  </p>
                </div>
              )}

              {mosaicStyle.shape === "zodiac" && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 22 }}>
                  {MOSAIC_ZODIAC.map(function(z) {
                    var on = mosaicStyle.zodiac === z.id;
                    return (
                      <button key={z.id} title={z.dates} onClick={function() { setMS({ zodiac: z.id }); }} style={{
                        padding: "8px 12px", background: on ? t.accentMuted : "transparent",
                        border: "1px solid " + (on ? t.accent : t.border), color: on ? t.text : t.textMuted,
                        fontSize: 10, fontFamily: SANS, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 6,
                      }}>
                        <span style={{ fontSize: 14 }}>{z.glyph}</span>{z.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {mosaicStyle.shape === "logo" && (
                <div style={{ marginBottom: 22 }}>
                  <label style={{
                    display: "inline-block", padding: "10px 20px", background: "transparent",
                    border: "1px dashed " + t.border, color: t.textSoft, fontSize: 10,
                    fontFamily: SANS, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                  }}>
                    {mosaicStyle.logoData ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} style={{ display: "none" }} />
                  </label>
                  {logoNote && <p style={{ fontFamily: SANS, fontSize: 9, color: logoNote.bad ? "#C87070" : t.textMuted, margin: "10px 0 0", lineHeight: 1.6 }}>{logoNote.text}</p>}
                  {mosaicStyle.logoData && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      <button onClick={function() { setMS({ logoInvert: true }); }} style={chipStyle(t, mosaicStyle.logoInvert !== false)}>Dark logo</button>
                      <button onClick={function() { setMS({ logoInvert: false }); }} style={chipStyle(t, mosaicStyle.logoInvert === false)}>Light logo</button>
                      <button onClick={function() { setMS({ logoData: null }); setLogoNote(null); }} style={chipStyle(t, false)}>Remove</button>
                    </div>
                  )}
                  <p style={{ fontFamily: SANS, fontSize: 9, color: t.textMuted, margin: "10px 0 0", lineHeight: 1.6 }}>
                    Bold, single-colour marks work. Thin or highly detailed logos turn to mush at poster size. Only upload a logo you have the right to print.
                  </p>
                </div>
              )}

              <p style={secLabel(t)}>Fill</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                <button onClick={function() { setMS({ invert: false }); }} style={chipStyle(t, mosaicStyle.invert !== true)}>Words inside</button>
                <button onClick={function() { setMS({ invert: true }); }} style={chipStyle(t, mosaicStyle.invert === true)}>Words around</button>
              </div>
              <p style={{ fontFamily: SANS, fontSize: 9, color: t.textMuted, margin: "0 0 22px", lineHeight: 1.6 }}>
                Inverted, the words fill the whole plate and the shape is cut out of them &mdash; the mark reads as empty space.
              </p>

              <p style={secLabel(t)}>Ink</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 22 }}>
                {MOSAIC_INKS.map(function(c) {
                  var on = mosaicStyle.ink === c.color;
                  return <button key={c.id} title={c.label} onClick={function() { setMS({ ink: c.color }); }} style={{
                    width: 34, height: 34, background: c.color, cursor: "pointer",
                    border: on ? "2px solid " + t.accent : "1px solid rgba(140,140,140,0.35)",
                    transform: on ? "scale(1.1)" : "scale(1)", transition: "all 0.2s",
                  }} />;
                })}
                <label style={swatchPicker(t)}>
                  +
                  <input type="color" value={mosaicStyle.ink} onChange={function(e) { setMS({ ink: e.target.value }); }}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
                </label>
              </div>

              <p style={secLabel(t)}>Ground</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {MOSAIC_GROUNDS.map(function(c) {
                  var on = mosaicStyle.ground === c.color;
                  return <button key={c.id} title={c.label} onClick={function() { setMS({ ground: c.color }); }} style={{
                    width: 34, height: 34, background: c.color, cursor: "pointer",
                    border: on ? "2px solid " + t.accent : "1px solid rgba(140,140,140,0.35)",
                    transform: on ? "scale(1.1)" : "scale(1)", transition: "all 0.2s",
                  }} />;
                })}
                <label style={swatchPicker(t)}>
                  +
                  <input type="color" value={mosaicStyle.ground} onChange={function(e) { setMS({ ground: e.target.value }); }}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
                </label>
              </div>
              <div style={{ marginBottom: 22 }}>
                <div style={{ height: 3, background: "rgba(140,140,140,0.2)", marginBottom: 6 }}>
                  <div style={{ height: 3, width: Math.min(100, (mosaicContrast / 12) * 100) + "%", background: mosaicTone, transition: "all 0.3s" }} />
                </div>
                <p style={{ fontFamily: SANS, fontSize: 9, color: mosaicTone, margin: 0, letterSpacing: 1 }}>{mosaicNote}</p>
              </div>

              <p style={secLabel(t)}>Density</p>
              <SliderRow t={t} label="Words per inch" value={mosaicStyle.density} min={0.7} max={1.4} step={0.05}
                display={Math.round(mosaicStyle.density * 100) + "%"} onChange={function(v) { setMS({ density: v }); }} />
              <p style={{ fontFamily: SANS, fontSize: 9, color: t.textMuted, margin: "0 0 22px", lineHeight: 1.6 }}>
                Higher density means smaller text and less of it cut by the edges. Lower density means bigger, bolder words and more slicing.
              </p>

              <p style={secLabel(t)}>Credit line</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <button onClick={function() { setMS({ showCredit: true }); }} style={chipStyle(t, mosaicStyle.showCredit !== false)}>Show names</button>
                <button onClick={function() { setMS({ showCredit: false }); }} style={chipStyle(t, mosaicStyle.showCredit === false)}>Hide names</button>
              </div>
              <p style={{ fontFamily: SANS, fontSize: 10, color: t.textMuted, margin: "0 0 14px", lineHeight: 1.6 }}>
                Quotes are never attributed inside the shape &mdash; at this size it would be unreadable. Names appear once, at the foot. The shared link still shows who said what.
              </p>

              <button onClick={function() { setMosaicStyle(Object.assign({}, MOSAIC_DEFAULTS)); setLogoNote(null); }} style={{
                padding: "8px 18px", background: "transparent", border: "1px solid " + t.border,
                color: t.textMuted, fontSize: 9, fontFamily: SANS, cursor: "pointer", letterSpacing: 2, textTransform: "uppercase",
              }}>Reset</button>
            </div>
          )}

          {/* ── GRID ACCORDION ── */}
          {designOpen && crystalStyle === "grid" && (
            <div style={{ marginTop: 20, padding: "24px", background: t.cardBg, border: "1px solid " + t.border, animation: "ytFadeIn 0.3s ease-out both" }}>

              <p style={{ fontFamily: SANS, fontSize: 10, color: t.textMuted, margin: "0 0 20px", lineHeight: 1.6 }}>
                One cell per contributor. The layout reflows as people are added &mdash; two columns at four voices, six at thirty.
              </p>

              <p style={secLabel(t)}>Paper</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 22 }}>
                {CUSTOM_BACKGROUNDS.map(function(b) {
                  var on = gridStyle.ground === b.color;
                  return <button key={b.id} title={b.label} onClick={function() { setGS({ ground: b.color }); }} style={{
                    width: 34, height: 34, background: b.color, cursor: "pointer",
                    border: on ? "2px solid " + t.accent : "1px solid rgba(140,140,140,0.35)",
                    transform: on ? "scale(1.1)" : "scale(1)", transition: "all 0.2s",
                  }} />;
                })}
                <label style={swatchPicker(t)}>
                  +
                  <input type="color" value={gridStyle.ground} onChange={function(e) { setGS({ ground: e.target.value }); }}
                    style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }} />
                </label>
              </div>

              <p style={secLabel(t)}>Ink</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {CUSTOM_TEXT_COLORS.map(function(c) {
                  var on = (gridStyle.ink || "auto") === (c.color || "auto");
                  return <button key={c.id} title={c.label} onClick={function() { setGS({ ink: c.color || "auto" }); }} style={{
                    width: 34, height: 34, cursor: "pointer",
                    background: c.color || "linear-gradient(135deg, #FFF 0 50%, #141014 50% 100%)",
                    border: on ? "2px solid " + t.accent : "1px solid rgba(140,140,140,0.35)",
                    transform: on ? "scale(1.1)" : "scale(1)", transition: "all 0.2s",
                  }} />;
                })}
              </div>
              <div style={{ marginBottom: 22 }}>
                <div style={{ height: 3, background: "rgba(140,140,140,0.2)", marginBottom: 6 }}>
                  <div style={{ height: 3, width: Math.min(100, (gridContrast / 12) * 100) + "%", background: gridTone, transition: "all 0.3s" }} />
                </div>
                <p style={{ fontFamily: SANS, fontSize: 9, color: gridTone, margin: 0, letterSpacing: 1 }}>{gridNote}</p>
              </div>

              <p style={secLabel(t)}>Text size</p>
              <SliderRow t={t} label="Cells" value={gridStyle.quoteScale} min={0.7} max={1.4} step={0.05}
                display={Math.round(gridStyle.quoteScale * 100) + "%"} onChange={function(v) { setGS({ quoteScale: v }); }} />
              <p style={{ fontFamily: SANS, fontSize: 9, color: t.textMuted, margin: "0 0 22px", lineHeight: 1.6 }}>
                Quotes shrink to fit their cell rather than being cut. Nothing anyone wrote gets dropped.
              </p>

              <p style={secLabel(t)}>Show</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                <button onClick={function() { setGS({ showRelationship: gridStyle.showRelationship === false }); }}
                  style={chipStyle(t, gridStyle.showRelationship !== false)}>How they know them</button>
                <button onClick={function() { setGS({ rules: gridStyle.rules === false }); }}
                  style={chipStyle(t, gridStyle.rules !== false)}>Rules between cells</button>
              </div>

              <button onClick={function() { setGridStyle(Object.assign({}, GRID_DEFAULTS)); }} style={{
                padding: "8px 18px", background: "transparent", border: "1px solid " + t.border,
                color: t.textMuted, fontSize: 9, fontFamily: SANS, cursor: "pointer", letterSpacing: 2, textTransform: "uppercase",
              }}>Reset</button>
            </div>
          )}
        </div>
      )}

      {/* ── STYLE SELECT — Character Select Grid ── */}
      {step === "done" && crystalData && (
        <div style={{ padding: "32px 16px 0", animation: "ytFadeIn 0.6s ease-out both", animationDelay: "0.3s" }}>
          <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, color: t.accentSoft, letterSpacing: 6, textTransform: "uppercase", textAlign: "center", marginBottom: 20 }}>Choose Your Style</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 10, maxWidth: 560, margin: "0 auto" }}>
            {STYLES.map(function(s) {
              var active = crystalStyle === s.id;
              var isLight = s.light === true
                || (s.id === "custom" && luminance(customStyle.bg) > 0.42)
                || (s.id === "grid" && luminance(gridStyle.ground) > 0.42)
                || (s.id === "mosaic" && luminance(mosaicStyle.ground) > 0.42);
              var chipInk = s.id === "custom" ? resolvedTextColor
                : s.id === "mosaic" ? mosaicStyle.ink
                : s.id === "grid" ? gridInk
                : (isLight ? "#3D2A4A" : "#C9A96E");
              return (
                <button key={s.id} onClick={function() { setCrystalStyle(s.id); }} style={{
                  position: "relative",
                  padding: "16px 8px 12px",
                  background: s.id === "custom" ? customStyle.bg : s.id === "mosaic" ? mosaicStyle.ground : s.id === "grid" ? gridStyle.ground : s.bg,
                  border: active ? "2px solid #C9A96E" : "1px solid " + (isLight ? "rgba(90,60,80,0.15)" : "rgba(184,150,122,0.12)"),
                  borderRadius: 4,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  textAlign: "center",
                  boxShadow: active ? "0 0 15px rgba(201,169,110,0.25), inset 0 0 15px rgba(201,169,110,0.08)" : "none",
                  transform: active ? "scale(1.05)" : "scale(1)",
                }}>
                  <div style={{ fontSize: 20, marginBottom: 6, opacity: active ? 1 : 0.5, color: chipInk, transition: "opacity 0.3s", display: "flex", justifyContent: "center", alignItems: "center", height: 24 }}>
                    {s.icon === "grid_svg" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        {[[3,3],[13,3],[3,13],[13,13]].map(function(p, pi) {
                          return <g key={pi}>
                            <rect x={p[0]} y={p[1]} width="8" height="8" fill="none" stroke={chipInk} strokeWidth="0.9" opacity="0.55" />
                            <rect x={p[0] + 1.4} y={p[1] + 2} width="5.2" height="1" fill={chipInk} opacity="0.9" />
                            <rect x={p[0] + 1.4} y={p[1] + 4.2} width="3.4" height="0.8" fill={chipInk} opacity="0.5" />
                          </g>;
                        })}
                      </svg>
                    ) : s.icon === "mosaic_svg" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <mask id="ytMosaicChip">
                            <rect width="24" height="24" fill="#000" />
                            <text x="12" y="19" textAnchor="middle" fontSize="20" fontWeight="700" fill="#fff" fontFamily="Georgia, serif">A</text>
                          </mask>
                        </defs>
                        <g mask="url(#ytMosaicChip)">
                          {[3, 6, 9, 12, 15, 18, 21].map(function(yy) {
                            return <rect key={yy} x="0" y={yy - 2} width="24" height="1.6" fill={chipInk} opacity={0.55 + (yy % 5) * 0.08} />;
                          })}
                        </g>
                      </svg>
                    ) : s.icon === "custom_svg" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="9" cy="9.5" r="5.5" fill={chipInk} opacity="0.85" />
                        <circle cx="15" cy="9.5" r="5.5" fill={chipInk} opacity="0.45" />
                        <circle cx="12" cy="15" r="5.5" fill={chipInk} opacity="0.25" />
                      </svg>
                    ) : s.icon === "starmap_svg" ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L13.5 9.5L20 8L14.5 12L20 16L13.5 14.5L12 22L10.5 14.5L4 16L9.5 12L4 8L10.5 9.5L12 2Z" fill={chipInk} />
                      </svg>
                    ) : s.icon}
                  </div>
                  <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: active ? 600 : 400, color: (s.id === "custom" || s.id === "mosaic" || s.id === "grid") ? chipInk : (isLight ? "#2A1F2A" : (active ? "#F5F0EB" : "#999")), letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 3, transition: "color 0.3s" }}>{s.label}</div>
                  <div style={{ fontFamily: SERIF, fontSize: 7, fontStyle: "italic", color: (s.id === "custom" || s.id === "mosaic" || s.id === "grid") ? chipInk : (isLight ? "#5C3A4A" : (active ? "#C9A96E" : "#555")), opacity: active ? 0.7 : 0.4 }}>{s.desc}</div>
                  {active && <div style={{ position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)", width: 20, height: 2, background: "#C9A96E", borderRadius: 1 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── ORDER SUCCESS ── */}
      {ordered && (
        <div style={{ textAlign: "center", padding: "24px 24px", margin: "0 auto", maxWidth: 480, animation: "ytFadeIn 0.6s ease-out both" }}>
          <div style={{ padding: "28px 32px", background: t.cardBg, border: "1px solid " + t.accentMuted }}>
            <p style={{ fontFamily: PLAY, fontSize: 22, fontStyle: "italic", fontWeight: 400, color: t.text, margin: "0 0 10px" }}>Your crystal is being crafted.</p>
            <p style={{ fontFamily: SANS, fontSize: 12, fontWeight: 300, color: t.textMuted, margin: 0, letterSpacing: 1 }}>We'll email you when it ships. Thank you.</p>
          </div>
        </div>
      )}

      {/* ── ORDER CTA ── */}
      {step === "done" && crystalData && !ordered && (
        <div style={{ textAlign: "center", padding: "40px 24px 8px", animation: "ytFadeIn 0.8s ease-out both", animationDelay: "0.4s" }}>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 24, flexWrap: "wrap" }}>
            {PRODUCT_OPTIONS.map(function(p) {
              var on = productKey === p.key;
              return (
                <button key={p.key} onClick={function() { setProductKey(p.key); }} style={{
                  padding: "14px 24px", minWidth: 150, textAlign: "center", cursor: "pointer",
                  background: on ? t.accentMuted : "transparent",
                  border: "1px solid " + (on ? t.accent : t.border),
                  transition: "all 0.25s",
                }}>
                  <div style={{ fontFamily: SANS, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: on ? t.text : t.textSoft, marginBottom: 5 }}>{p.label}</div>
                  <div style={{ fontFamily: PLAY, fontSize: 20, color: on ? t.text : t.textMuted }}>${p.price}</div>
                </button>
              );
            })}
          </div>
          <button onClick={handleCheckout} disabled={ordering} className="yt-cta" style={{
            ...btnStyle(t), padding: "18px 60px", fontSize: 12,
            opacity: ordering ? 0.5 : 1, cursor: ordering ? "wait" : "pointer",
          }}>{ordering ? "Redirecting to checkout\u2026" : (productKey === "poster" ? "Order Poster" : "Order Frame")}</button>
          <p style={{ marginTop: 16, fontSize: 11, color: t.textMuted, fontFamily: SANS, fontWeight: 300, letterSpacing: 2 }}>
            {(PRODUCT_OPTIONS.filter(function(p) { return p.key === productKey; })[0] || PRODUCT_OPTIONS[0]).blurb}
          </p>
        </div>
      )}

      {/* ── ADMIN: Export engraving file ── */}
      {isAdmin && step === "done" && crystalData && (
        <div style={{ textAlign: "center", padding: "16px 24px 0", animation: "ytFadeIn 0.5s ease-out both" }}>
          <button onClick={exportEngraving} style={{
            padding: "12px 32px", background: "transparent",
            border: "1px solid rgba(200,100,100,0.3)", color: "rgba(200,100,100,0.7)",
            fontSize: 10, fontFamily: SANS, fontWeight: 400, letterSpacing: 3,
            textTransform: "uppercase", cursor: "pointer",
          }}>Download Engraving PNG ({crystalStyle})</button>
          <button onClick={exportTextFile} style={{
            padding: "12px 32px", background: "transparent", marginLeft: 12,
            border: "1px solid rgba(100,150,200,0.3)", color: "rgba(100,150,200,0.7)",
            fontSize: 10, fontFamily: SANS, fontWeight: 400, letterSpacing: 3,
            textTransform: "uppercase", cursor: "pointer",
          }}>Download Text File</button>
          <button onClick={function() { downloadPDF(compositorRef.current, constellation.recipient_name); }} style={{
            padding: "12px 32px", background: "transparent", marginLeft: 12,
            border: "1px solid rgba(120,180,100,0.3)", color: "rgba(120,180,100,0.7)",
            fontSize: 10, fontFamily: SANS, fontWeight: 400, letterSpacing: 3,
            textTransform: "uppercase", cursor: "pointer",
          }}>Download PDF</button>
          <p style={{ marginTop: 8, fontSize: 10, color: t.textMuted, fontFamily: SANS, fontWeight: 300 }}>Admin only</p>
        </div>
      )}

      {/* ── DIVIDER ── */}
      {compiled && <div style={{ padding: "48px 0 0" }}><LineDivider t={t} /></div>}

      {/* ── DECODE ── */}
      {compiled && (
        <div style={{ maxWidth: 500, margin: "0 auto", padding: "56px 24px 0", animation: "ytFadeIn 0.8s ease-out both", animationDelay: "0.5s" }}>
          <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, color: t.accentSoft, letterSpacing: 8, textTransform: "uppercase", textAlign: "center", marginBottom: 48 }}>Decode</p>

          {compiled.clusters.map(function(cl, ci) {
            return (
              <div key={ci} style={{ marginBottom: 40 }}>
                <p style={{ fontFamily: SERIF, fontSize: 15, fontStyle: "italic", fontWeight: 300, color: t.textSoft, margin: "0 0 16px" }}>{cl.name}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {cl.stars.map(function(star, si) {
                    var isActive = selectedStar === star.label;
                    return (
                      <button key={si} className="yt-pill" onClick={function() { setSelectedStar(isActive ? null : star.label); }} style={{
                        padding: "8px 20px",
                        background: isActive ? t.accentMuted : t.cardBg,
                        border: "1px solid " + (isActive ? t.accent : t.border),
                        color: isActive ? t.text : t.textSoft,
                        fontSize: 13, fontFamily: SANS, fontWeight: isActive ? 400 : 300,
                        letterSpacing: isActive ? 0.5 : 0, cursor: "pointer",
                      }}>{star.label}</button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {sel && (
            <div style={{ marginTop: 8, padding: "28px", background: t.cardBg, border: "1px solid " + t.accentMuted, animation: "ytFadeIn 0.3s ease-out both" }}>
              <p style={{ fontFamily: SERIF, fontSize: 17, fontStyle: "italic", fontWeight: 300, color: t.textSoft, lineHeight: 1.9, margin: 0 }}>{sel.decode}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
                <div style={{ width: 20, height: 1, background: t.accentMuted }} />
                <p style={{ fontFamily: SANS, fontSize: 9, fontWeight: 300, color: t.accentSoft, margin: 0, letterSpacing: 2 }}>{sel.clusterName}{sel.contributors && sel.contributors.length > 0 ? " \u00B7 " + sel.contributors.join(", ") : ""}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── COPY LINK ── */}
      <div style={{ textAlign: "center", padding: "60px 24px 80px" }}>
        <button onClick={copyLink} className="yt-pill" style={{
          ...btnStyle(t, "secondary"), padding: "14px 36px", fontSize: 10,
        }}>{copied ? "copied" : "copy contribute link"}</button>
      </div>
    </PageWrap>
  );
}
